export const SUPPORTED_LOCALES = ['en-US', 'pt-BR', 'es-419'] as const

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]

/**
 * Used only when no browser preference can be resolved to a supported locale.
 * A `NULL` stored preference means "not known yet" and must never be read as English.
 */
export const FALLBACK_LOCALE: SupportedLocale = 'en-US'

export function isSupportedLocale(candidateLocale: string): candidateLocale is SupportedLocale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(candidateLocale)
}

const PRIMARY_LANGUAGE_TO_SUPPORTED_LOCALE: Readonly<Record<string, SupportedLocale>> = {
  en: 'en-US',
  pt: 'pt-BR',
  es: 'es-419',
}

function matchSingleLanguageTag(languageTag: string): SupportedLocale | null {
  const trimmedLanguageTag = languageTag.trim()
  if (trimmedLanguageTag.length === 0) {
    return null
  }

  const exactMatch = SUPPORTED_LOCALES.find(
    (supportedLocale) => supportedLocale.toLowerCase() === trimmedLanguageTag.toLowerCase(),
  )
  if (exactMatch !== undefined) {
    return exactMatch
  }

  const primaryLanguage = trimmedLanguageTag.split('-')[0]?.toLowerCase() ?? ''
  return PRIMARY_LANGUAGE_TO_SUPPORTED_LOCALE[primaryLanguage] ?? null
}

/**
 * Resolves an ordered list of browser language tags to the closest supported locale.
 * Examples: `pt-PT` resolves to `pt-BR`, `es-MX` resolves to `es-419`, `en-GB` to `en-US`.
 */
export function resolveSupportedLocaleFromLanguagePreferences(
  languageTags: readonly string[],
): SupportedLocale {
  for (const languageTag of languageTags) {
    const matchedLocale = matchSingleLanguageTag(languageTag)
    if (matchedLocale !== null) {
      return matchedLocale
    }
  }
  return FALLBACK_LOCALE
}

/**
 * Parses an HTTP `Accept-Language` header into language tags ordered by descending quality.
 */
export function parseAcceptLanguageHeader(headerValue: string | undefined | null): string[] {
  if (typeof headerValue !== 'string' || headerValue.trim().length === 0) {
    return []
  }

  return headerValue
    .split(',')
    .map((rawLanguageRange, declarationPosition) => {
      const [languageTag, ...parameters] = rawLanguageRange.trim().split(';')
      const qualityParameter = parameters.find((parameter) => parameter.trim().startsWith('q='))
      const parsedQuality =
        qualityParameter === undefined ? 1 : Number.parseFloat(qualityParameter.trim().slice(2))
      return {
        languageTag: (languageTag ?? '').trim(),
        quality: Number.isFinite(parsedQuality) ? parsedQuality : 0,
        declarationPosition,
      }
    })
    .filter((languageRange) => languageRange.languageTag.length > 0 && languageRange.quality > 0)
    .sort(
      (firstRange, secondRange) =>
        secondRange.quality - firstRange.quality ||
        firstRange.declarationPosition - secondRange.declarationPosition,
    )
    .map((languageRange) => languageRange.languageTag)
    .filter((languageTag) => languageTag !== '*')
}

/**
 * Marks a deployment that serves every supported language and lets the browser decide,
 * as opposed to the usual one deployment per language.
 */
export const MULTILINGUAL_DEPLOYMENT_MARKER = 'intl'

export const APPLICATION_LOCALE_SETTINGS = [
  ...SUPPORTED_LOCALES,
  MULTILINGUAL_DEPLOYMENT_MARKER,
] as const

/** What `APPLICATION_LOCALE` accepts: one supported locale, or the multilingual marker. */
export type ApplicationLocaleSetting = (typeof APPLICATION_LOCALE_SETTINGS)[number]

/**
 * The single language a deployment serves, or `null` when it serves all of them.
 * `null` is the only state in which browser preferences and stored preferences matter.
 */
export function resolveDeploymentLocaleFromSetting(
  applicationLocaleSetting: ApplicationLocaleSetting,
): SupportedLocale | null {
  return applicationLocaleSetting === MULTILINGUAL_DEPLOYMENT_MARKER
    ? null
    : applicationLocaleSetting
}
